/**
 * The one prerequisite selector, shared by the proposal form and the
 * edit-dependencies dialog: search, a checkbox list, and the selection as
 * removable chips, with loading, failure and emptiness stated explicitly.
 */

import { useState, type ReactNode } from 'react';

import type { TaskSummary } from '../api.js';
import { Button, Tag, TextField } from '../ui.js';
import { TaskStateChip } from './task-status-badges.js';
import { useWorkspaceCopy } from './use-workspace-copy.js';

/** What the picker knows about its candidate list. Never a silent absence. */
export type DependencyCandidates =
  | { readonly status: 'loading' }
  | { readonly status: 'failed' }
  | { readonly status: 'ready'; readonly tasks: readonly TaskSummary[] };

/**
 * Choose prerequisite tasks: a search box over reference and title, a
 * checkbox list that shows enough to tell tasks apart — reference, title,
 * type, current state — and the current selection as removable chips that
 * never scroll out of sight.
 *
 * The three list states are explicit. Loading says loading, failure says so
 * and offers a retry, and emptiness is a sentence — the old picker simply
 * vanished in all three cases, which made "the list is stale" and "there
 * are no tasks" the same picture.
 */
export function DependencyPicker({
  legend,
  hint,
  candidates,
  selected,
  onToggle,
  onRetry,
}: {
  readonly legend: string;
  readonly hint: string;
  readonly candidates: DependencyCandidates;
  readonly selected: readonly string[];
  readonly onToggle: (taskId: string, wanted: boolean) => void;
  readonly onRetry: () => void;
}): ReactNode {
  const copy = useWorkspaceCopy();
  const [query, setQuery] = useState('');

  const all = candidates.status === 'ready' ? candidates.tasks : [];
  const needle = query.trim().toLowerCase();
  const matching =
    needle.length === 0
      ? all
      : all.filter(
          (task) =>
            task.reference.toLowerCase().includes(needle) ||
            task.title.toLowerCase().includes(needle),
        );
  const chosen = selected
    .map((id) => all.find((task) => task.id === id))
    .filter((task): task is TaskSummary => task !== undefined);

  return (
    <fieldset style={{ border: 'none', margin: '0 0 var(--space-md)', padding: 0 }}>
      <legend
        style={{
          font: 'var(--type-caption)',
          letterSpacing: 'var(--type-caption-tracking)',
          color: 'var(--text-secondary)',
          marginBottom: 4,
          padding: 0,
        }}
      >
        {legend}
      </legend>
      <p
        style={{
          font: 'var(--type-caption)',
          color: 'var(--text-muted)',
          margin: '0 0 8px',
        }}
      >
        {hint}
      </p>

      {candidates.status === 'loading' ? (
        <p
          role="status"
          style={{ font: 'var(--type-body-md)', color: 'var(--text-muted)', margin: 0 }}
        >
          {copy.propose.prerequisitesLoading}
        </p>
      ) : null}

      {candidates.status === 'failed' ? (
        <div
          role="alert"
          style={{
            display: 'flex',
            gap: 10,
            alignItems: 'center',
            flexWrap: 'wrap',
            font: 'var(--type-body-md)',
            color: 'var(--status-error-fg)',
          }}
        >
          <span>{copy.propose.prerequisitesFailed}</span>
          <Button size="sm" variant="outline" onClick={onRetry}>
            {copy.actions.retry}
          </Button>
        </div>
      ) : null}

      {candidates.status === 'ready' && all.length === 0 ? (
        <p
          style={{ font: 'var(--type-body-md)', color: 'var(--text-muted)', margin: 0 }}
        >
          {copy.propose.prerequisitesEmpty}
        </p>
      ) : null}

      {candidates.status === 'ready' && all.length > 0 ? (
        <>
          {chosen.length > 0 ? (
            <div style={{ marginBottom: 10 }}>
              <p
                style={{
                  font: 'var(--type-caption)',
                  color: 'var(--text-secondary)',
                  margin: '0 0 6px',
                }}
              >
                {copy.propose.selectedPrerequisites(chosen.length)}
              </p>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {chosen.map((task) => (
                  <span
                    key={task.id}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      border: '1px solid var(--border-hairline)',
                      borderRadius: 999,
                      padding: '2px 6px 2px 10px',
                      font: 'var(--type-caption)',
                      color: 'var(--text-primary)',
                      background: 'var(--surface-soft)',
                    }}
                  >
                    <span
                      className="mj-ltr"
                      dir="ltr"
                      style={{ font: 'var(--type-body-tabular)' }}
                    >
                      {task.reference}
                    </span>
                    <button
                      type="button"
                      aria-label={copy.propose.removePrerequisite(task.reference)}
                      onClick={() => {
                        onToggle(task.id, false);
                      }}
                      style={{
                        border: 'none',
                        background: 'transparent',
                        cursor: 'pointer',
                        color: 'var(--text-muted)',
                        font: 'var(--type-caption)',
                        lineHeight: 1,
                        padding: 4,
                      }}
                    >
                      <svg
                        viewBox="0 0 12 12"
                        width="10"
                        height="10"
                        aria-hidden="true"
                      >
                        <path
                          d="M3 3l6 6M9 3l-6 6"
                          stroke="currentColor"
                          strokeWidth="1.6"
                          strokeLinecap="round"
                        />
                      </svg>
                    </button>
                  </span>
                ))}
              </div>
            </div>
          ) : null}

          <TextField
            label={copy.propose.prerequisitesSearchLabel}
            value={query}
            onChange={setQuery}
            placeholder={copy.propose.prerequisitesSearchPlaceholder}
          />

          {matching.length === 0 ? (
            <p
              style={{
                font: 'var(--type-body-md)',
                color: 'var(--text-muted)',
                margin: 0,
              }}
            >
              {copy.propose.prerequisitesNoMatches}
            </p>
          ) : (
            <div
              style={{
                maxHeight: 240,
                overflowY: 'auto',
                border: '1px solid var(--border-hairline)',
                borderRadius: 'var(--radius-card)',
              }}
            >
              {matching.map((task) => (
                <label
                  key={task.id}
                  style={{
                    display: 'flex',
                    gap: 10,
                    alignItems: 'center',
                    padding: '8px 12px',
                    cursor: 'pointer',
                    borderBlockEnd: '1px solid var(--border-hairline)',
                    minHeight: 44,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(task.id)}
                    onChange={(event) => {
                      onToggle(task.id, event.target.checked);
                    }}
                  />
                  <span
                    className="mj-ltr"
                    dir="ltr"
                    style={{
                      font: 'var(--type-body-tabular)',
                      color: 'var(--text-muted)',
                      flexShrink: 0,
                    }}
                  >
                    {task.reference}
                  </span>
                  <span
                    style={{
                      font: 'var(--type-body-md)',
                      color: 'var(--text-primary)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      flexGrow: 1,
                      minWidth: 0,
                    }}
                  >
                    {task.title}
                  </span>
                  <Tag tone="neutral" title={copy.task.type}>
                    {copy.types[task.taskType] ?? task.taskType}
                  </Tag>
                  <TaskStateChip status={task.status} />
                </label>
              ))}
            </div>
          )}
        </>
      ) : null}
    </fieldset>
  );
}
